import Navbar from "@/components/Navbar";
import TabBar from "@/components/TabBar";
import Footer from "@/components/Footer";
import AnalyticsConsent from "@/components/AnalyticsConsent";
import { trackPageView } from "@/lib/analytics";
import { API } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";

function VerifyEmailBanner() {
  const { user, refreshUser } = useAuth();
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified !== false || pathname.startsWith("/login") || pathname.startsWith("/admin")) {
    return null;
  }

  const resend = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/resend-verify`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not resend.");
      toast.success(data.message || "Verification email sent.");
      await refreshUser();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative z-20 border-b border-gp-gold/30 bg-gp-gold/10 px-5 py-2.5 text-sm text-center" data-testid="verify-email-banner">
      Check your inbox to verify {user.email}.{" "}
      <button type="button" onClick={resend} disabled={busy} className="font-semibold text-gp-gold hover:underline disabled:opacity-60">
        {busy ? "Sending…" : "Resend link"}
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);

  const isAdmin = pathname.startsWith("/admin");

  return (
    <div className={`min-h-screen flex flex-col text-white relative overflow-x-hidden ${isAdmin ? "bg-[#080b10]" : "bg-gp-bg"}`}>
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-60"
        style={{
          background:
            "radial-gradient(900px 500px at 15% -10%, rgba(0,157,255,0.12), transparent), radial-gradient(700px 500px at 95% 10%, rgba(240,192,64,0.10), transparent), radial-gradient(800px 600px at 50% 120%, rgba(155,89,182,0.10), transparent)",
        }}
      />
      {isAdmin ? (
        <header className="relative z-20 border-b border-white/10 bg-black/40 backdrop-blur">
          <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 flex items-center justify-between">
            <a href="/admin" className="font-unbounded font-black tracking-tight">GamePlat <span className="text-gp-gold">Admin</span></a>
            <div className="flex items-center gap-4 text-sm">
              <a href="/" className="text-white/60 hover:text-white">View website</a>
              <a href="/profile" className="text-white/60 hover:text-white">Account</a>
            </div>
          </div>
        </header>
      ) : (
        <>
          <Navbar />
          <TabBar />
          <VerifyEmailBanner />
          <AnalyticsConsent />
        </>
      )}
      <main className="relative z-10 flex-1">{children}</main>
      {!isAdmin && <Footer />}
    </div>
  );
}
